'use client';

import { LifeProduct } from '@app/domain/model/eshop/life/LifeProduct';
import { Select } from '@app/app/_components/Input/Select/Select';
import { SelectOption } from '@app/app/_components/Input/Select/SelectOption';
import { useState, useMemo } from 'react';
import { useTranslations } from 'next-intl';
import { useChangeEffect } from '@app/lib/use-change-effect/useChangeEffect';
import { CloseOutlinedCircleIcon } from '@app/app/_icons/CloseOutlinedCircleIcon';
import { twJoin } from 'tailwind-merge';
import { CallbackButton } from '@app/app/_components/CallbackButton/CallbackButton';
import { Message } from '@app/app/_components/Message/Message';
import { ViolationMap } from '@app/lib/form/contracts/ViolationMap';
import { ViolationMessage } from '@app/lib/form/contracts/ViolationMessage';

interface Props {
    jobs: LifeProduct[];
    errorMessage?: ViolationMap | ViolationMessage;
    defaultValue?: number[];
    onProfessionChange?: () => void;
}

export function ProfessionStep({ jobs, errorMessage, defaultValue, onProfessionChange }: Props) {
    const t = useTranslations();
    const [selectedJobs, setSelectedJobs] = useState<string[]>(
        defaultValue ? defaultValue.map(jobId => jobId.toString()) : [],
    );
    const [searchQuery, setSearchQuery] = useState('');

    const jobOptions: SelectOption[] = useMemo(
        () =>
            jobs.map(job => ({
                value: job.code,
                label: job.value,
            })),
        [jobs],
    );

    const filteredOptions = useMemo(() => {
        const availableOptions = jobOptions.filter(option => !selectedJobs.includes(option.value));

        if (!searchQuery) {
            return availableOptions;
        }

        return availableOptions.filter(option => option.label.toLowerCase().includes(searchQuery.toLowerCase()));
    }, [jobOptions, selectedJobs, searchQuery]);

    useChangeEffect(() => {
        onProfessionChange?.();
    }, selectedJobs);

    const handleJobSelect = (value: string) => {
        if (value && !selectedJobs.includes(value)) {
            setSelectedJobs(prev => [...prev, value]);
        }
        setSearchQuery('');
    };

    const handleRemoveJob = (jobId: string) => {
        setSelectedJobs(prev => prev.filter(id => id !== jobId));
    };

    const getJobLabel = (jobId: string) => {
        return jobs.find(job => job.code === jobId)?.value ?? jobId;
    };

    return (
        <div>
            <div className="mb-8">
                <Select
                    name="jobs"
                    placeholder={t('eshop.life.professionPlaceholder')}
                    label={t('eshop.life.startWritingYourProfession')}
                    options={filteredOptions}
                    error={errorMessage}
                    value=""
                    onChange={handleJobSelect}
                    searchable
                    onSearch={setSearchQuery}
                    noResultsHint={t('eshop.life.noJobsFound')}
                />

                {selectedJobs.map((jobId, index) => (
                    <input key={jobId} type="hidden" name={`jobs[${index}]`} value={jobId} />
                ))}

                {selectedJobs.length > 0 && (
                    <div className="mt-4 flex flex-wrap gap-2">
                        {selectedJobs.map(jobId => (
                            <div
                                key={jobId}
                                className={twJoin(
                                    'flex items-center gap-2 rounded-sm',
                                    'border border-neutral-300 bg-white',
                                    'px-4 text-sm',
                                )}>
                                <span>{getJobLabel(jobId)}</span>
                                <CallbackButton
                                    ignoreFormStatus
                                    variant="text"
                                    id={jobId}
                                    onClick={handleRemoveJob}
                                    className="m-0 flex h-min w-min items-center justify-center p-0 text-neutral-500 transition-colors hover:text-red-500">
                                    <CloseOutlinedCircleIcon className="size-5" />
                                </CallbackButton>
                            </div>
                        ))}
                    </div>
                )}
            </div>

            <Message variant="info" className="font-normal">
                <div dangerouslySetInnerHTML={{ __html: t.raw('eshop.life.professionMessage') }} />
            </Message>
        </div>
    );
}
